<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import { useDisplay } from 'vuetify';
import { useCustomizerStore } from '@/shared/stores/customizer';
import HorizontalItems from './horizontalItems';
import NavItem from './NavItem/Index.vue';
import NavCollapse from './NavCollapse/Index.vue';
import VerticalSidebar from '../vertical-sidebar/VerticalSidebar.vue';
import { Icon } from '@iconify/vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useRouter, useRoute } from 'vue-router';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { usePreSetStore } from '@/features/startTask/preset_task/shared/preset/stores/preSetStore';
import { checkMenuViewPermission } from '@/shared/utils/permissionUtils';
import { shouldApplyDeptMenuVisibilityForJurisdiction } from '@/shared/utils/deptMenuVisibility';

const customizer = useCustomizerStore();
const sessionStore = useSessionStore();
const preSetStore = usePreSetStore();
const sidebarMenu = shallowRef(HorizontalItems);
const { mdAndUp } = useDisplay();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);
const router = useRouter();
const route = useRoute();

// Start Task 버튼인지 확인
const isStartTaskButton = (item: any) => {
    return item.button && item.buttonLabel === 'startTask';
};

// 버튼 라벨 가져오기
const getButtonLabel = (item: any) => {
    if (!item.buttonLabel) return '';
    return t('menu.' + item.buttonLabel) || item.buttonLabel;
};

// Start Task 버튼 클릭 핸들러
const handleStartTaskClick = (item: any) => {
    if (typeof item.buttonAction === 'string') {
        const targetPath = item.buttonAction;
        const currentPath = route.path;

        // ★ Start Task (/tasks/new) 인터셉트: MY 관할권은 현재 페이지 위에 PreSet 모달 노출
        if (targetPath === '/tasks/new') {
            const j = sessionStore.user?.customer?.jurisdiction;
            const isMy = j === 'MY' || j === 'MALAYSIA';
            if (isMy) {
                preSetStore.openModal();
                return;
            }
        }

        // 같은 라우트로 이동하는 경우 데이터 재조회
        if (currentPath === targetPath) {
            router.replace({ path: targetPath, query: { _refresh: Date.now() } });
        } else {
            router.push({ path: targetPath, query: {} });
        }
    } else if (typeof item.buttonAction === 'function') {
        item.buttonAction();
    }
};

const filteredSidebarMenu = computed(() => {
    const customerInfo = sessionStore.getCustomerInfo();
    const jurisdiction = String(customerInfo?.jurisdiction || '').toUpperCase();
    const isMYJurisdiction = jurisdiction === 'MY' || jurisdiction === 'MALAYSIA';
    const shouldApplyDeptMenuVisibility = shouldApplyDeptMenuVisibilityForJurisdiction(jurisdiction);

    const mappedMenu = (sidebarMenu.value as any[])
        .filter((item) => {
            if (!item) return false;
            if (item.title === 'Current Register') return false;
            if (item.to === '/register/current') return false;
            if (item.icon === 'book-bookmark-linear') return false;
            return true;
        })
        .map(item => {
        if (item.title === 'Entity') {
            return {
                ...item,
                to: isMYJurisdiction ? '/entity_my' : '/entity'
            };
        }

        // 운영 Register: MY → Register Book, SG 등 → 기존 /register
        if (item.title === 'Register') {
            return {
                ...item,
                to: isMYJurisdiction ? '/register/my' : '/register',
            };
        }

        return item;
    });

    if (!shouldApplyDeptMenuVisibility) return mappedMenu;

    const permissions = sessionStore.getPermissions as any;

    const filterByView = (items: any[]): any[] => {
        return items
            .map(item => {
                if (item.button) {
                    if (typeof item.buttonAction === 'string') {
                        if (item.title === 'Account Settings') return item;
                        const canView = checkMenuViewPermission(permissions, item.buttonAction);
                        return canView ? item : null;
                    }
                    return item;
                }

                if (item.children && Array.isArray(item.children)) {
                    const filteredChildren = filterByView(item.children);
                    if (filteredChildren.length === 0) return null;
                    return { ...item, children: filteredChildren };
                }

                if (!item.to || item.to === '#') return item;
                if (item.title === 'Account Settings') return item;

                const canView = checkMenuViewPermission(permissions, item.to);
                return canView ? item : null;
            })
            .filter(Boolean);
    };

    return filterByView(mappedMenu);
});

</script>

<template>
    <template v-if="mdAndUp">
        <div class="horizontalMenu  border-bottom bg-surface position-relative">
            <div :class="customizer.boxed ? 'maxWidth' : 'px-6'">
                <ul class="gap-3 horizontal-navbar mx-lg-0 mx-3">
                    <!---Menu Loop -->
                    <li v-for="(item, i) in filteredSidebarMenu" :key="i" class="navItem">
                        <!---Button Item (Start Task) -->
                        <template v-if="item.button && isStartTaskButton(item)">
                            <router-link
                                :to="typeof item.buttonAction === 'string' ? item.buttonAction : '#'"
                                class="navItemLink single-link rounded-pill"
                                style="display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background-color: rgb(var(--v-theme-primary)); color: white; text-decoration: none;"
                                @click="handleStartTaskClick(item)"
                            >
                                <i class="navIcon">
                                    <Icon
                                        v-if="item.buttonIcon"
                                        :icon="item.buttonIcon"
                                        height="18"
                                        width="18"
                                    />
                                </i>
                                <span>{{ getButtonLabel(item) }}</span>
                            </router-link>
                        </template>
                        <!---If Has Child -->
                        <NavCollapse :item="item" :level="0" v-else-if="item.children"  />
                        <!---Single Item-->
                        <NavItem :item="item" v-else />
                        <!---End Single Item-->
                    </li>
                </ul>
            </div>    
        </div>
    </template>
    <div v-else class="mobile-menu">
        <VerticalSidebar />
    </div>
</template>
<style lang="scss"></style>
